import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, Search, FileDown, Music, Upload } from 'lucide-react';
import { TEACHINGS, HERO_IMAGES } from '../mock';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import VideoModal from '../components/VideoModal';
import TeachingUploadDialog from '../components/TeachingUploadDialog';
import api, { fileUrl } from '../lib/api';
import { toSlug } from '../lib/slug';

export default function Teachings() {
  const [query, setQuery] = useState('');
  const [series, setSeries] = useState('All');
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [remote, setRemote] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(() => {
    api.listTeachings().then(list => {
      const mapped = list.filter(u => u.video_url).map(u => ({
        id: u.id,
        title: u.title,
        speaker: u.speaker || 'Pastor Oladipo Babalola',
        duration: u.duration || '',
        series: u.series || 'Teaching',
        thumbnail: u.thumbnail_url ? fileUrl(u.thumbnail_url) : HERO_IMAGES.crossSunset,
        videoUrl: fileUrl(u.video_url),
        audioUrl: u.audio_url ? fileUrl(u.audio_url) : null,
        pdfUrl: u.pdf_url ? fileUrl(u.pdf_url) : null,
        pdfName: u.pdf_name || null,
        description: u.description || '',
        isLocal: true,
        uploaded: true,
      }));
      setRemote(mapped);
    }).catch(() => setRemote([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const merged = useMemo(() => [...remote, ...TEACHINGS], [remote]);

  const allSeries = useMemo(() => ['All', ...Array.from(new Set(merged.map(t => t.series)))], [merged]);
  const filtered = useMemo(() => merged.filter(t => {
    const matchSeries = series === 'All' || t.series === series;
    const q = query.trim().toLowerCase();
    const matchQ = !q || t.title.toLowerCase().includes(q) || t.speaker.toLowerCase().includes(q);
    return matchSeries && matchQ;
  }), [query, series, merged]);

  const featured = merged.find(t => t.isLocal);

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.bibleStudy2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/80 to-[#0B1B27]/50" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-4">Watch &amp; Grow</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="font-serif-display text-5xl md:text-7xl leading-tight">Teachings from the Word</h1>
              <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl">
                Christ-centred messages, unpacked plainly, offered warmly. Come, listen, and let the Word do its beautiful work.
              </p>
            </div>
            <Button onClick={() => setUploadOpen(true)} className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11 px-5 self-start md:self-end whitespace-nowrap">
              <Upload className="h-4 w-4 mr-2" /> Publish Teaching
            </Button>
          </div>
        </div>
      </section>

      {featured && (
        <section className="py-16 md:py-20 bg-ivory">
          <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative group cursor-pointer" onClick={() => { setActiveVideo(featured); setVideoOpen(true); }}>
              <div className="absolute -inset-2 bg-gradient-to-br from-gold-brand/30 to-teal-brand/30 rounded-2xl blur-xl opacity-80" />
              <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black">
                <video src={featured.videoUrl} className="w-full h-full object-cover" muted loop playsInline autoPlay />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                    <Play className="h-8 w-8 text-teal-deep fill-teal-deep ml-1" />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Badge className="bg-gold-brand text-ink font-medium mb-3">Featured &middot; {featured.series}</Badge>
              <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">{featured.title}</h2>
              <p className="mt-3 text-teal-deep font-medium">{featured.speaker}{featured.duration ? ` · ${featured.duration}` : ''}</p>
              <p className="mt-5 text-lg text-ink/70 leading-relaxed">{featured.description}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                <Button className="bg-teal-brand hover:bg-teal-deep text-white h-11 px-5" onClick={() => { setActiveVideo(featured); setVideoOpen(true); }}>
                  <Play className="mr-2 h-4 w-4" /> Watch Now
                </Button>
                <Link to={`/teachings/series/${toSlug(featured.series)}`}>
                  <Button variant="outline" className="h-11 px-5 border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white">
                    View Full Series
                  </Button>
                </Link>
                {featured.audioUrl && (
                  <a href={featured.audioUrl} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="h-11 px-5 border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white"><Music className="h-4 w-4 mr-2" /> Listen (MP3)</Button>
                  </a>
                )}
                {featured.pdfUrl && (
                  <a href={featured.pdfUrl} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="h-11 px-5 border-gold-brand text-gold-deep hover:bg-gold-brand hover:text-ink"><FileDown className="h-4 w-4 mr-2" /> Notes (PDF)</Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-10">
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink">Message Library</h2>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
              <Input placeholder="Search teachings…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 bg-white" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-8">
            {allSeries.map(s => (
              <button key={s} onClick={() => setSeries(s)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${series === s ? 'bg-teal-deep text-white' : 'bg-white text-ink/70 border border-ink/10 hover:border-teal-brand hover:text-teal-deep'}`}>
                {s}
              </button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <p className="text-ink/50 text-center py-16">No teachings match your search.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(t => (
                <Card key={t.id} className="hover-lift group overflow-hidden bg-white rounded-2xl border border-ink/5 cursor-pointer" onClick={() => t.isLocal ? (setActiveVideo(t), setVideoOpen(true)) : null}>
                  <div className="relative aspect-video overflow-hidden bg-black">
                    <img src={t.thumbnail} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-14 w-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="h-6 w-6 text-teal-deep fill-teal-deep ml-0.5" />
                      </div>
                    </div>
                    <Badge className="absolute top-3 left-3 bg-gold-brand text-ink font-medium">{t.series}</Badge>
                    {t.duration && <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/60 text-white text-xs font-medium">{t.duration}</div>}
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif-display text-xl leading-snug text-ink group-hover:text-teal-deep transition-colors">{t.title}</h3>
                    <p className="text-xs tracking-[0.2em] uppercase text-ink/50 mt-2">{t.speaker}</p>
                    <p className="text-sm text-ink/60 mt-2 line-clamp-2">{t.description}</p>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap gap-1.5">
                        {t.audioUrl && <Badge variant="secondary" className="bg-cream text-teal-deep border border-teal-brand/20 text-xs">MP3</Badge>}
                        {t.pdfUrl && <Badge variant="secondary" className="bg-cream text-gold-deep border border-gold-brand/20 text-xs">PDF Notes</Badge>}
                      </div>
                      <Link to={`/teachings/series/${toSlug(t.series)}`} onClick={(e) => e.stopPropagation()} className="text-xs font-semibold text-teal-deep hover:underline">
                        See series &rarr;
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} videoUrl={activeVideo?.videoUrl} title={activeVideo?.title} isLocal={activeVideo?.isLocal} />
      <TeachingUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
    </div>
  );
}
